import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import type { City } from 'api/api.types'
import { getBottomMenu, getCities, getSelectedCity, getTopMenu } from 'src/store/data.store'
import {
  getIsAuthenticated,
  getIsFirstStart,
  getPage,
  setSelectedCityId,
  signOut,
} from 'src/store/app.state.store'
import { getBrowserGeolocation, getGpsDistance, parseCoords } from 'utils/geo'
import { Popup } from 'components/Utility/Popup/Popup'
import { OrderButton } from 'components/Utility/OrderButton/OrderButton'
import { Social } from 'components/PageElements/Social/Social'
import { MobileMenu } from 'components/PageElements/MobileMenu/MobileMenu'
import { CurrentCity } from 'components/PageElements/CurrentCity/CurrentCity'
import { Account } from 'components/Profile/Account/Account'
import styles from './Header.module.scss'

const findClosestCity = (cities: City[], lat: number, lng: number): City | undefined => {
  let closest = cities[0]
  let minDistance = Infinity
  cities.forEach((city) => {
    const coords = parseCoords(city.coords)
    if (!coords) {
      return
    }
    const distance = getGpsDistance(coords[0], coords[1], lat, lng)
    if (distance < minDistance) {
      closest = city
      minDistance = distance
    }
  })
  return closest
}

export const Header: React.FC = () => {
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isFirstStart = useSelector(getIsFirstStart)
  const topMenu = useSelector(getTopMenu)
  const bottomMenu = useSelector(getBottomMenu)
  const selectedCity = useSelector(getSelectedCity)
  const cities = useSelector(getCities)
  const page = useSelector(getPage)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [isCitiesPickerActive, setCitiesPickerActive] = useState(false)
  const [isUserPopupActive, setUserPopupActive] = useState(false)
  const [isBurgerMenuActive, setBurgerMenuActive] = useState(false)

  const goToAppointment = useCallback(() => {
    navigate('#appointment')
  }, [navigate])

  const hideProfilePreview = useCallback(() => {
    setUserPopupActive(false)
  }, [])

  const goToProfile = useCallback(() => {
    if (isAuthenticated) {
      hideProfilePreview()
      navigate('#profile')
    }
  }, [navigate, isAuthenticated, hideProfilePreview])

  const handleSignOutClick = useCallback(() => {
    hideProfilePreview()
    navigate('#')
    dispatch(signOut())
  }, [navigate, dispatch, hideProfilePreview])

  const handleAccountClick = useCallback(() => {
    if (isAuthenticated) {
      setUserPopupActive(true)
    } else {
      navigate('#signin')
    }
  }, [navigate, isAuthenticated])

  // On the very first visit pick the closest city, or ask the client to choose
  useEffect(() => {
    if (!isFirstStart) {
      return
    }
    getBrowserGeolocation()
      .then((result) => {
        const closest = result && findClosestCity(cities, result[0], result[1])
        if (closest) {
          dispatch(setSelectedCityId(closest.city.id))
        } else {
          setCitiesPickerActive(true)
        }
      })
      .catch(() => {
        setCitiesPickerActive(true)
      })
  }, [isFirstStart, cities, dispatch])

  return (
    <div className={styles.container}>
      <OrderButton className={styles.appointmentButtonMobile} onClick={goToAppointment} />
      <div className={styles.main}>
        {page?.template !== 'salon-page' && (
          <div className={styles.socialIcons}>
            <div className={styles.socialIcon}>
              <Social link={selectedCity?.socials.vk} type={'vk'} place={'header'} />
            </div>
            <div className={styles.socialIcon}>
              <Social link={selectedCity?.socials.tg} type={'telegram'} place={'header'} />
            </div>
          </div>
        )}
        <div className={styles.currentCity}>
          <CurrentCity
            active={isCitiesPickerActive}
            onClick={() => setCitiesPickerActive(true)}
            onClosePicker={() => setCitiesPickerActive(false)}
          />
        </div>
        <div className={styles.account}>
          <Account
            previewActive={isUserPopupActive}
            onClickSignOut={handleSignOutClick}
            onClickAppointment={goToAppointment}
            onClickProfile={goToProfile}
            onClick={handleAccountClick}
            onClosePreview={hideProfilePreview}
          />
        </div>
        <OrderButton className={styles.appointmentButton} onClick={goToAppointment} />
        <div className={styles.mobileMenuContainer}>
          <i className={styles.mobileMenuIcon} onClick={(e) => {
            e.stopPropagation()
            setBurgerMenuActive(true)
          }} />
          <Popup
            className={styles.burgerMobile}
            place={'burger'}
            active={isBurgerMenuActive}
            onClose={() => setBurgerMenuActive(false)}
          >
            <MobileMenu
              topMenu={topMenu}
              bottomMenu={bottomMenu}
              onClose={() => setBurgerMenuActive(false)}
            />
          </Popup>
        </div>
      </div>
    </div>
  )
}
